import { createGlobalState } from '@vueuse/core';
import { ref, watch } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { outboundMessagesAnalyticsClient } from '~/services';

export const useAnalyticsSelectResources = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;
    const { getCurrentGroupId, currentOrgUnit } = useCurrentGroup();

    // used to retrieve the name of resources
    const cachedResources = ref([]);

    const selectingResources = ref(false);
    const selectedResources = ref([]);

    watch(
        selectedResources,
        () => {
            // whenever selected resources is changed update the cache
            cachedResources.value = cachedResources.value.concat(
                selectedResources.value.filter(
                    (selected) =>
                        // only add to the cache
                        // if contains name
                        selected.resource_name &&
                        // if not already cached
                        !cachedResources.value.some((cached) => isSameResource(selected, cached))
                )
            );
        },
        {
            deep: true,
        }
    );

    const { loading: loadingOptions, response: options, createSearch, cancelKey } = useSearchRequest();

    // TODO: Add default value to useSearchRequest, make the first argument of useSearchRequest be an object with keys, instead of multiple arguments.
    options.value = [];

    const fetchSelectedResources = async (fetchParams) => {
        const missingResources = selectedResources.value
            .filter((selected) => !cachedResources.value.some((cached) => isSameResource(selected, cached)))
            .map(({ resource_type, resource_id }) => ({
                resource_type,
                resource_id,
            }));

        if (!missingResources.length) {
            return;
        }

        const params = {
            range: fetchParams.range,
            filters: {
                resources: missingResources,
            },
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveResources(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId(),
            },
            params
        );

        cachedResources.value = cachedResources.value.concat(response.data.data);
    };

    const searchFn = createSearch(async (fetchParams, query) => {
        const params = {
            ...fetchParams,
            filters: {
                ...fetchParams.filters,
                resources: undefined,
            },
            query: query,
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveResources(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId(),
            },
            params,
            { cancelKey }
        );

        if (!query) {
            fetchSelectedResources(fetchParams);
        }

        return response.data.data;
    });

    const isSameResource = (a, b) => a.resource_type === b.resource_type && a.resource_id === b.resource_id;

    const resourceName = (resource) =>
        cachedResources.value.find((option) => isSameResource(resource, option))?.resource_name;

    return {
        selectingResources,
        selectedResources,
        loadingOptions,
        options,
        searchFn,
        isSameResource,
        resourceName,
    };
});
